import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import GenericModal from '../../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../../enums/modal-size.enum';
import usePostFactory from '../../../api/factories/usePostFactory';
import useWarehouseActions from '../../api/useWarehouseActions';
import { toast } from 'react-toastify';

const EMPTY_WAREHOUSE = {
  name: '',
  address: '',
  city: '',
  postal_code: '',
  latitude: '',
  longitude: '',
  external_id: '',
};

const Field = ({ label, hint, required, children }) => (
  <label className="wf-field">
    <span className="wf-field__label">
      {label}
      {required && <span className="wf-field__required">requis</span>}
    </span>
    {children}
    {hint && <span className="wf-field__hint">{hint}</span>}
  </label>
);

/**
 * Fiche entrepôt : création, ou modification quand `warehouse` (ligne de
 * GET /factories) est fourni
 */
export default function CreateWarehouseModal({
  isOpen,
  setIsOpen,
  onSuccess,
  warehouse = null,
}) {
  const isEdition = Boolean(warehouse);
  const [warehouseData, setWarehouseData] = useState(EMPTY_WAREHOUSE);
  const [addressQuery, setAddressQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showManualCoordinates, setShowManualCoordinates] = useState(false);
  const searchTimeoutRef = useRef(null);

  const { isLoading: isCreating, postFactory } = usePostFactory();
  const { updateWarehouse, isSaving } = useWarehouseActions();
  const isLoading = isCreating || isSaving;

  // Modification : la fiche reprend l'entrepôt
  useEffect(() => {
    if (!isOpen) return;
    setWarehouseData(
      warehouse
        ? {
            name: warehouse.name || '',
            address: warehouse.street || '',
            city: warehouse.city || '',
            postal_code: warehouse.zipcode || '',
            latitude: warehouse.latitude ?? '',
            longitude: warehouse.longitude ?? '',
            external_id: warehouse.external_id ?? '',
          }
        : EMPTY_WAREHOUSE
    );
  }, [isOpen, warehouse]);

  // Recherche d'adresse (Nominatim / OpenStreetMap) avec anti-rebond. La
  // requête en vol est annulée à chaque frappe : une réponse lente pour une
  // saisie précédente n'écrase pas les résultats de la dernière
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (addressQuery.length <= 2) {
      setSearchResults([]);
      setIsSearching(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(addressQuery)}&countrycodes=fr&limit=5`,
          { headers: { 'Accept-Language': 'fr' }, signal: controller.signal }
        );
        setSearchResults(response.data);
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error('Erreur lors de la recherche géographique:', error);
        setSearchResults([]);
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 500);

    return () => {
      controller.abort();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [addressQuery]);

  const update = (field, value) =>
    setWarehouseData((prev) => ({ ...prev, [field]: value }));

  const handleClose = () => {
    setIsOpen(false);
    setWarehouseData(EMPTY_WAREHOUSE);
    setAddressQuery('');
    setSearchResults([]);
    setShowManualCoordinates(false);
  };

  const handleSelectAddress = (result) => {
    const details = result.address || {};
    const addressParts = result.display_name.split(', ');

    setWarehouseData((prev) => ({
      ...prev,
      address:
        [details.house_number, details.road].filter(Boolean).join(' ') ||
        addressParts[0] ||
        '',
      city:
        details.city ||
        details.town ||
        details.village ||
        details.municipality ||
        '',
      postal_code: details.postcode || '',
      latitude: result.lat,
      longitude: result.lon,
    }));
    setAddressQuery('');
    setSearchResults([]);
  };

  const clearAddress = () =>
    setWarehouseData((prev) => ({
      ...prev,
      address: '',
      city: '',
      postal_code: '',
      latitude: '',
      longitude: '',
    }));

  const handleSubmit = async () => {
    const payload = {
      name: warehouseData.name.trim(),
      street: warehouseData.address || null,
      city: warehouseData.city || null,
      zipcode: warehouseData.postal_code || null,
      latitude:
        warehouseData.latitude !== ''
          ? parseFloat(warehouseData.latitude)
          : null,
      longitude:
        warehouseData.longitude !== ''
          ? parseFloat(warehouseData.longitude)
          : null,
      external_id:
        warehouseData.external_id !== ''
          ? parseInt(warehouseData.external_id, 10)
          : null,
    };
    if (isEdition) {
      try {
        await updateWarehouse(warehouse.id, payload);
        handleClose();
        if (onSuccess) onSuccess();
      } catch (error) {
        toast.error(error.message);
      }
      return;
    }
    try {
      await postFactory({
        name: warehouseData.name.trim(),
        street: warehouseData.address || null,
        city: warehouseData.city || null,
        zipcode: warehouseData.postal_code || null,
        latitude: warehouseData.latitude
          ? parseFloat(warehouseData.latitude)
          : null,
        longitude: warehouseData.longitude
          ? parseFloat(warehouseData.longitude)
          : null,
        external_id: warehouseData.external_id
          ? parseInt(warehouseData.external_id, 10)
          : null,
      });
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erreur lors de la création de l'entrepôt:", error);
    }
  };

  const hasAddress = Boolean(warehouseData.address);
  const hasCoordinates = Boolean(
    warehouseData.latitude && warehouseData.longitude
  );

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title={
        isEdition
          ? `Modifier l'entrepôt ${warehouse.name}`
          : 'Créer un entrepôt'
      }
      mainAction={handleSubmit}
      mainActionText={isEdition ? 'Enregistrer' : "Créer l'entrepôt"}
      mainActionDisabled={warehouseData.name.trim() === ''}
      mainActionIsLoading={isLoading}
      secondaryAction={handleClose}
      secondaryActionText="Annuler"
      size={MODAL_SIZE_STYLE[MODAL_SIZE.LG]}
    >
      <div className="wf">
        <section className="wf-section">
          <h3 className="wf-section__title">Identification</h3>
          <div className="wf-grid">
            <Field label="Nom de l'entrepôt" required>
              <input
                className="wf-input"
                value={warehouseData.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Ex : Entrepôt Paris Nord"
                autoFocus={!isEdition}
              />
            </Field>
            <Field
              label="Code externe"
              hint="Facultatif · identifiant dans votre ERP"
            >
              <input
                className="wf-input"
                type="number"
                value={warehouseData.external_id}
                onChange={(e) => update('external_id', e.target.value)}
                placeholder="Ex : 1001"
              />
            </Field>
          </div>
        </section>

        <section className="wf-section">
          <h3 className="wf-section__title">Adresse</h3>
          <p className="wf-section__description">
            L&apos;adresse place l&apos;entrepôt sur la carte et sert de point
            de départ des tournées.
          </p>

          {hasAddress ? (
            <div className="wf-address">
              <span className="wf-address__icon">▣</span>
              <div className="wf-address__text">
                <span className="wf-address__line">
                  {warehouseData.address}
                </span>
                <span className="wf-address__meta">
                  {[warehouseData.postal_code, warehouseData.city]
                    .filter(Boolean)
                    .join(' ')}
                  {hasCoordinates &&
                    ` · ${parseFloat(warehouseData.latitude).toFixed(
                      5
                    )}, ${parseFloat(warehouseData.longitude).toFixed(5)}`}
                </span>
              </div>
              <button type="button" className="wf-link" onClick={clearAddress}>
                Changer
              </button>
            </div>
          ) : (
            <div className="wf-geocoder">
              <span className="wf-geocoder__icon">⌕</span>
              <input
                className="wf-input wf-input--search"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                placeholder="Rechercher une adresse…"
                aria-label="Rechercher une adresse"
              />
              {isSearching && <span className="wf-spinner" />}
              {searchResults.length > 0 && (
                <div className="wf-geocoder__results">
                  {searchResults.map((result) => (
                    <button
                      type="button"
                      key={result.place_id}
                      className="wf-geocoder__result"
                      onClick={() => handleSelectAddress(result)}
                    >
                      {result.display_name}
                    </button>
                  ))}
                </div>
              )}
              {addressQuery.length > 2 &&
                !isSearching &&
                searchResults.length === 0 && (
                  <span className="wf-field__hint">
                    Aucune adresse trouvée — vous pouvez saisir les coordonnées
                    à la main ci-dessous.
                  </span>
                )}
            </div>
          )}

          <button
            type="button"
            className="wf-disclosure"
            onClick={() => setShowManualCoordinates((prev) => !prev)}
            aria-expanded={showManualCoordinates}
          >
            <span
              className={`wf-disclosure__caret ${
                showManualCoordinates ? 'wf-disclosure__caret--open' : ''
              }`}
            >
              ›
            </span>
            Saisir les coordonnées GPS à la main
          </button>

          {showManualCoordinates && (
            <div className="wf-grid">
              <Field label="Latitude">
                <input
                  className="wf-input"
                  type="number"
                  step="any"
                  value={warehouseData.latitude}
                  onChange={(e) => update('latitude', e.target.value)}
                  placeholder="48.856614"
                />
              </Field>
              <Field label="Longitude">
                <input
                  className="wf-input"
                  type="number"
                  step="any"
                  value={warehouseData.longitude}
                  onChange={(e) => update('longitude', e.target.value)}
                  placeholder="2.352222"
                />
              </Field>
            </div>
          )}

          {!hasCoordinates && (
            <p className="wf-warning">
              Sans coordonnées, l&apos;entrepôt n&apos;apparaîtra pas sur la
              carte et ne pourra pas servir de départ à l&apos;optimisation.
            </p>
          )}
        </section>
      </div>
    </GenericModal>
  );
}
